"use client";

import { useCallback, useState, useRef } from "react";

import Message from "../components/dialog/Message";

/**
 * メッセージダイアログを管理する拡張カスタムフック
 * useAlertのラッパーとして機能し、追加の機能を提供します
 * @returns {Object} アラート状態と制御関数
 */
export function useMessageEx() {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const resolvePromiseRef = useRef(null);

  const showMessage = useCallback(
    (title, message, severity = "info") => {
      //console.log(`showMessage isOpen: ${isOpen}, title: ${title}, message: ${message}, severity: ${severity}`);
      return new Promise((resolve) => {
        setTitle(title || "");
        setMessage(message);
        setSeverity(severity);
        resolvePromiseRef.current = resolve;
        setIsOpen(true);
      });
    },
    [setIsOpen]
  );

  /**
   * アラートを表示（警告用）
   * 後方互換性のため、複数の呼び出し形式に対応
   * @param {string|Object} arg1 - タイトル、メッセージ、またはオブジェクト
   * @param {string} [arg2] - メッセージ（arg1がタイトルの場合）
   * @param {string} [arg3] - severity（arg1がタイトル、arg2がメッセージの場合）
   * @returns {Promise} アラートが閉じられたときに解決されるPromise
   */
  const showAlert = useCallback(
    (arg1, arg2, arg3) => {
      // オブジェクト形式の場合: { title, message, severity }
      if (typeof arg1 === "object" && arg1 !== null) {
        const { title = "重要", message, severity = "warning" } = arg1;
        return showMessage(title, message, severity);
      }
      // 1引数の場合: showAlert(message)
      if (arg2 === undefined) {
        return showMessage("重要", arg1, "warning");
      }
      // 2引数の場合: showAlert(title, message) - 既存の形式を維持
      if (arg3 === undefined) {
        return showMessage(arg1, arg2, "warning");
      }
      // 3引数の場合: showAlert(title, message, severity)
      return showMessage(arg1, arg2, arg3);
    },
    [showMessage]
  );

  const closeAlert = useCallback(() => {
    setIsOpen(false);
    if (resolvePromiseRef.current) {
      resolvePromiseRef.current(0);
      resolvePromiseRef.current = null;
    }
  }, [setIsOpen]);

  /**
   * 成功アラートを表示
   * @param {string} message - 表示するメッセージ
   * @param {string} [title="Success"] - ダイアログのタイトル
   */
  const showSuccess = useCallback(
    (message, title = "成功") => {
      showMessage(title, message, "success");
    },
    [showMessage]
  );

  /**
   * エラーアラートを表示
   * @param {string} message - 表示するメッセージ
   * @param {string} [title="Error"] - ダイアログのタイトル
   */
  const showError = useCallback(
    (message, title = "エラー") => {
      showMessage(title, message, "error");
    },
    [showMessage]
  );

  /**
   * 警告アラートを表示
   * @param {string} message - 表示するメッセージ
   * @param {string} [title="Warning"] - ダイアログのタイトル
   */
  const showWarning = useCallback(
    (message, title = "警告") => {
      showMessage(title, message, "warning");
    },
    [showMessage]
  );

  /**
   * 情報アラートを表示
   * @param {string} message - 表示するメッセージ
   * @param {string} [title="Information"] - ダイアログのタイトル
   */
  const showInfo = useCallback(
    (message, title = "情報") => {
      showMessage(title, message, "info");
    },
    [showMessage]
  );

  const onResolve = useCallback(
    (buttonType) => {
      setIsOpen(false);
      if (resolvePromiseRef.current) {
        resolvePromiseRef.current(buttonType);
        resolvePromiseRef.current = null;
      }
    },
    [setIsOpen]
  );

  const renderMessageDialog = useCallback(() => {
    if (!isOpen) return null;
    return <Message isOpen={isOpen} title={title} message={message} severity={severity} onResolve={onResolve} />;
  }, [isOpen, title, message, severity, onResolve]);

  return {
    alert: { isOpen, title, message, severity },
    showMessage,
    showAlert,
    closeAlert,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    renderMessageDialog,
  };
}
